<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { isEmpty } from '@utils';

import { VueRecaptcha } from 'vue-recaptcha';
import LayoutValidation from '@layouts/LayoutValidation.vue';
import ValidateAccessTermResult from '@views/ValidateAccessTermResult.vue';
import { XSpinner, XButton, XTextInput } from '@clicksign/excalibur';

const props = defineProps({
  recaptchaSiteKey: {
    type: String,
    default: '',
  },
  encriptedAccessToken: {
    type: String,
    default: '',
  },
});

const store = useStore();
const { t } = useI18n();

const accessToken = ref('');
const isDocumentValid = ref(false);
const isLoading = ref(false);
const errorMessage = ref('');
const validRecaptcha = ref(true);

const displayRecaptcha = computed(() => {
  return !isEmpty(props.recaptchaSiteKey);
});

const disabledValidationButton = computed(() => {
  if (!displayRecaptcha.value) return false;
  return validRecaptcha.value;
});

async function submit() {
  try {
    isLoading.value = true;

    const isValid = await store.dispatch('validateAccess/validadeAccessToken', accessToken.value);

    if (isValid) {
      isDocumentValid.value = true;
    }
  } catch (error) {
    errorMessage.value = error[0];
    validRecaptcha.value = true;
  } finally {
    isLoading.value = false;
  }
}

function onVerify(response) {
  if (response !== undefined) {
    validRecaptcha.value = false;
  }
}

onMounted(() => {
  accessToken.value = atob(props.encriptedAccessToken);
});
</script>

<template>
  <LayoutValidation
    icon="key"
    :show-result="isDocumentValid"
    color="var(--color-neutral-700)"
  >
    <template #title>
      {{ t('accessTerm.generate_password.title') }}
    </template>

    <template #description>
      <p>{{ t('accessTerm.generate_password.subtitle') }}</p>
    </template>

    <template #action>
      <div
        v-if="!isLoading"
        :class="$style.container"
      >
        <XTextInput
          v-model="accessToken"
          :class="$style.input"
          required
          maxlength="10"
          data-testid="accessTokenField"
          :message="errorMessage"
        />

        <VueRecaptcha
          v-if="displayRecaptcha"
          :class="$style.recaptcha"
          :sitekey="recaptchaSiteKey"
          @verify="onVerify"
        />

        <XButton
          type="submit"
          :disabled="disabledValidationButton"
          data-testid="validateButton"
          @click="submit"
        >
          {{ t('accessTerm.generate_password.validate') }}
        </XButton>
      </div>
      <XSpinner
        v-if="isLoading"
        size="large"
        show-text
        data-testid="spinnerText"
        vertical
      >
        {{ t('validator.document.loading') }}
      </XSpinner>
    </template>

    <template #result>
      <ValidateAccessTermResult />
    </template>
  </LayoutValidation>
</template>

<style lang="scss" module>
.container {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: var(--space-regular);
}

.input {
  width: 300px;
}

.recaptcha {
  display: flex;
  justify-content: center;
  margin-bottom: var(--space-regular);
}
</style>
